import React, { useEffect, useState } from "react";
import { AspectRatio, Box, type LayoutProps, type SpaceProps } from "@chakra-ui/react";
import { type ImageProps, type StaticImageData } from "next/image";
import { isSafari } from "react-device-detect";
import { FillImage } from "components/images";
import { type BrandColorToken } from "theme/colors";
import { Lottie, type LottieProps } from "./lottie";

type LottieWithStaticSafariImageProps = {
  /**
   * Determines if the image should be preloaded.
   */
  isPriority?: boolean;

  /**
   * The image to display if the animation is not supported in Safari.
   */
  image: StaticImageData;

  /**
   * Determines the placeholder for the image.
   *
   * @default "empty"
   */
  imagePlaceholder?: ImageProps["placeholder"];

  /**
   * The alt text for the image.
   */
  alt: string;

  /**
   * Placeholder to show while hydration occurs.
   *
   * Defaults to a rounded box with the same aspect ratio as the animation.
   */
  placeholder?: JSX.Element;

  /**
   * Background color for the placeholder while hydration occurs.
   *
   * @default "new.gray.50"
   */
  placeholderBgColor?: BrandColorToken;

  /**
   * Optional props to add to the `AspectRatio` that wraps the static image.
   */
  staticContainerProps?: LayoutProps & SpaceProps;
} & LottieProps;

/**
 * A Lottie animation component that will alternatively render a static image in Safari only.
 * Use this when a Lottie animation does not play nicely in Safari (e.g. has lag).
 */
export function LottieWithStaticSafariImage({
  isPriority,
  image,
  imagePlaceholder,
  alt,
  placeholder,
  placeholderBgColor = "new.gray.50",
  staticContainerProps,
  ratio,
  ...lottieProps
}: LottieWithStaticSafariImageProps) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Resolve hydration issue and render a placeholder during client-side rendering
  if (!isClient) {
    return (
      placeholder ?? (
        <AspectRatio ratio={ratio} w="100%" borderRadius={{ base: "small", md: "bigger" }} overflow="hidden">
          <Box bg={placeholderBgColor} boxSize="100%" />
        </AspectRatio>
      )
    );
  }

  if (isSafari) {
    return (
      <AspectRatio
        ratio={ratio}
        w="100%"
        borderRadius={{ base: "small", md: "bigger" }}
        overflow="hidden"
        {...staticContainerProps}
      >
        <FillImage placeholder={imagePlaceholder} priority={isPriority} src={image} alt={alt} />
      </AspectRatio>
    );
  }

  return <Lottie ratio={ratio} {...lottieProps} />;
}
